import { createContext, useState, useContext, useRef } from 'react'
import { POST_DISPLAY } from '@utils/notificationUtils'
import { useAuthContext } from './authContext'
import useStateRef from 'react-usestateref'
import useLoading from '@hooks/useLoading'
import axios from '@utils/webRequests'
import minsAgo from '@utils/minsAgo'

const NotificationsContext = createContext();
const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([[]])
  const [displayNotifications, setDisplayNotifications, displayNotificationsRef] = useStateRef([])
  const { isLoading, setIsLoading, isLoadingDelayed } = useLoading(false)
  const [noMoreNotifications, setNoMoreNotifications] = useState(false)
  const [timeSinceLastLoad, setTimeSinceLastLoad] = useState(minsAgo(10))
  const firstRender = useRef(true)
  const isReloading = isLoading && displayNotifications?.length > 0

  const removeRedundancy = (notificationArr, altIndex) => {
    return notificationArr.filter((notificationObj) => {
      // Keep the object if it has more than one data to display
      if (notificationObj.data.length > 1) return notificationObj;
      else {
        // NOTE: We need to weed out redundant entries because we're creating two indices for the same data. So we'll get a lot of extra "single" notification objects
        const accessor = notificationObj?.data[0]?.uuid
        if (altIndex[accessor].data.length > 1) {
          // If the altIndex has more than one entry, keep that one since it has more entries to display
          return null
        } else {
          // If the altIndex display ALSO has only one data to display, then we prefer to keep the
          // current object, and not the alt object
          // NOTE: With this condition, the order in which we call removeRedundancy on the arrays determines
          //       which display gets kept (the objects from the first call are kept since we're removing those
          //       objects from the altIndex before we iterate through it)
          delete altIndex[accessor]
          return notificationObj
        }
      }
    })
  }

  const flattenIndices = (groupedByAction, groupedByUser) => {
    let groupedByActionArr = Object.values(groupedByAction)
    groupedByActionArr = removeRedundancy(groupedByActionArr, groupedByUser)
    const groupedByUserArr = removeRedundancy(Object.values(groupedByUser), groupedByAction)
    return groupedByActionArr.concat(groupedByUserArr)
  }

  const assignToIndex = ({ index, accessor, data, groupedBy, timestamp, seenAt, action, format }) => {
    if (index[accessor]?.data && index[accessor]?.timestamp) {
      // Unset seenAt if any of the data is null
      if (!seenAt) index[accessor].seenAt = null
      // Check for earliest timestamp to dipslay newest notifications
      const isEarlier = timestamp > index[accessor].timestamp
      if (isEarlier) {
        // unshift data to front of array
        index[accessor].data.unshift(data)
        // replace timestamp if earlier one is found
        index[accessor].timestamp = timestamp
      } else {
        // push new data to array
        index[accessor].data.push(data)
      }
    } else {
      // created new object for index
      index[accessor] = {
        timestamp,
        seenAt,
        data: [data],
        groupedBy,
        action,
        format,
      }
    }
  }

  const parseNotifications = (rawNotifications) => {
    const usersByLike = {}
    const likesByUser = {}
    const usersByRepost = {}
    const repostsByUser = {}
    const followsIndex = {}
    const posts = []

    rawNotifications.forEach((notification) => {
      const { action, originator, post, seenAt, createdAt } = notification || {}
      const parsedCreatedAt = new Date(createdAt)
      const parsedSeenAt = seenAt ? new Date(seenAt) : null
      if (POST_DISPLAY.has(action)) {
        notification.timestamp = parsedCreatedAt
        notification.seenAt = parsedSeenAt
        posts.push(notification)
      } else if (action === 'like') {
        assignToIndex({
          index: usersByLike,
          accessor: post?.uuid,
          data: originator,
          groupedBy: post,
          timestamp: parsedCreatedAt,
          seenAt: parsedSeenAt,
          format: 'users_by_like',
          action,
        })
        assignToIndex({
          index: likesByUser,
          accessor: originator?.uuid,
          data: post,
          groupedBy: originator,
          timestamp: parsedCreatedAt,
          seenAt: parsedSeenAt,
          format: 'likes_by_user',
          action,
        })
      } else if (action === 'repost') {
        assignToIndex({
          index: usersByRepost,
          accessor: post?.uuid,
          data: originator,
          groupedBy: post,
          timestamp: parsedCreatedAt,
          seenAt: parsedSeenAt,
          format: 'users_by_repost',
          action,
        })
        assignToIndex({
          index: repostsByUser,
          accessor: originator?.uuid,
          data: post,
          groupedBy: originator,
          timestamp: parsedCreatedAt,
          seenAt: parsedSeenAt,
          format: 'reposts_by_user',
          action,
        })
      } else if (action === 'follow') {
        assignToIndex({
          index: followsIndex,
          accessor: 'follow',
          data: originator,
          groupedBy: null,
          timestamp: parsedCreatedAt,
          seenAt: parsedSeenAt,
          format: 'follow',
          action,
        })
      }
    })

    let unsorted = flattenIndices(usersByLike, likesByUser)
    unsorted = unsorted.concat(flattenIndices(usersByRepost, repostsByUser))
    unsorted = unsorted.concat(Object.values(followsIndex))
    unsorted = unsorted.concat(posts)
    const parsed = unsorted.sort(({ timestamp: a }, { timestamp: b }) => a > b ? -1 : 1)
    return parsed
  }

  function getNotifications(options = { offset: 0 }) {
    setIsLoading(true)
    return axios.get('/api/my/notifications', { params: { offset: options?.offset || 0 } })
  }

  const getInitialNotifications = () => getNotifications()
    .then((res) => {
      setNotifications([res.data])
      setDisplayNotifications(parseNotifications(res.data))
      setIsLoading(false)
      setNoMoreNotifications(res.data.length < 100)
      firstRender.current = false
      setTimeSinceLastLoad(new Date())
      // NOTE: don't return this because we'd dont want the result passed to .then blocks chained to this
      axios.put('/api/my/notifications/update_seen_at').catch(console.log)
    })

  const getMoreNotifications = () => getNotifications({ offset: notifications?.length })
    .then((res) => {
      setNotifications(notifications.concat(res.data))
      setDisplayNotifications(displayNotifications.concat(parseNotifications(res.data)))
      setIsLoading(false)
      setNoMoreNotifications(res.data.length < 100)
    })

  return (
    <NotificationsContext.Provider value={{
      notifications,
      setNotifications,
      displayNotifications,
      setDisplayNotifications,
      displayNotificationsRef,

      notificationsLoaded: !isLoading,
      notificationsLoadingDelayed: isLoadingDelayed,
      setNotificationsLoaded: (isLoaded) => setIsLoading(!isLoaded),
      isNotificationsReloading: isReloading,

      noMoreNotifications,
      setNoMoreNotifications,

      getNotifications,
      getInitialNotifications,
      getMoreNotifications,
      firstRender,
      timeSinceLastLoad,
      setTimeSinceLastLoad,
    }}>
      {children}
    </NotificationsContext.Provider>
  )
}

const useNotificationsContext = () => useContext(NotificationsContext)

export default useNotificationsContext

export { NotificationsContext, NotificationsProvider, useNotificationsContext }
